import { useContext } from 'react';

import { Trans } from '@lingui/react/macro';
import { Checkbox } from '@repo/videa-components/src/index';
import { Label } from '@repo/videa-components/src/ui/label';
import classNames from 'classnames';
import { useAtom } from 'jotai';
import { useParams } from 'react-router-dom';

import { ExternalPatientContext } from '@/app/external/constants';
import {
  type ModelsToggles,
  toothPartsTogglesAtom,
} from '@/modules/annotator/findingSidebar/atoms';
import type { FindingsCategory } from '@/modules/annotator/types';
import { FINDINGS_CATEGORIES } from '@/modules/annotator/types';
import { useGetModelFeatureFlags } from '@/modules/annotator/utils';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { usePatientPrintoutData } from '../hooks/usePatientPrintoutData';

import { ThemeModeContext } from './QRPrintDownloadContent';

interface PreviewSidebarFindingsFilterProps {
  enabledFindingTypes: ModelsToggles;
  setEnabledFindingTypes: React.Dispatch<React.SetStateAction<ModelsToggles>>;
  findingTypes: { label: string; value: keyof ModelsToggles }[];
}

export const PreviewSidebarFindingsFilter = ({
  enabledFindingTypes,
  setEnabledFindingTypes,
  findingTypes,
}: PreviewSidebarFindingsFilterProps) => {
  const mode = useContext(ThemeModeContext);
  const {
    isIframe,
    externalPatientId,
    externalImageId,
    patientId,
    trialStatus,
    centralizedBilling: isCentralizedBilling,
  } = useContext(ExternalPatientContext);
  const { trackEvent } = useTrackEvent();
  const [toothPartsToggles, setToothPartsToggles] = useAtom(
    toothPartsTogglesAtom,
  );
  const { id: selectedPatientId } = useParams<{ id: string }>();

  const {
    isPrlEnabled,
    isRblEnabled,
    isCalculusEnabled,
    isCariesSubclassEnabled,
    isVdaEnabled,
  } = useGetModelFeatureFlags();

  const { mixPanelData } = usePatientPrintoutData();

  const selectedFindingsCategories: FindingsCategory[] =
    FINDINGS_CATEGORIES.filter(
      (category) =>
        (category === 'restorative' && enabledFindingTypes.caries) ||
        (category === 'incipients' && enabledFindingTypes.incipients) ||
        (category === 'periodontal' &&
          enabledFindingTypes.radiographic_boneloss) ||
        (category === 'endodontic' &&
          enabledFindingTypes.periapical_radiolucency) ||
        (category === 'chart' && toothPartsToggles.tooth_anatomy),
    );

  const handleToggleClick = (category: FindingsCategory, checked: boolean) => {
    trackEvent(mixpanelEvents.previewDialogFindingsToggle, {
      source: 'visit_sidebar',
      findingType: category,
      selected: checked,
      ...mixPanelData,
      isIframe: !!isIframe,
      externalPatientId,
      externalImageId,
      patientId: selectedPatientId ?? patientId,
      trialStatus,
      centralizedBilling: isCentralizedBilling,
    });

    const newList = checked
      ? [...selectedFindingsCategories, category]
      : selectedFindingsCategories.filter((item) => item !== category);

    const newFindingsTypes: ModelsToggles = {
      ...enabledFindingTypes,
      caries: newList.includes('restorative'),
      area_of_concern:
        isCariesSubclassEnabled && newList.includes('restorative'),
      restorative_adjacent_lucency:
        isVdaEnabled && newList.includes('restorative'),
      radiographic_boneloss: isRblEnabled && newList.includes('periodontal'),
      calculus: isCalculusEnabled && newList.includes('periodontal'),
      periapical_radiolucency: isPrlEnabled && newList.includes('endodontic'),
      incipients: newList.includes('incipients'),
    };
    setEnabledFindingTypes(newFindingsTypes);

    if (category === 'chart') {
      setToothPartsToggles({
        ...toothPartsToggles,
        tooth_anatomy: !toothPartsToggles.tooth_anatomy,
      });
    }
  };

  return (
    <div className="vai-text-foreground-dark vai-font-inter">
      <div
        className={classNames(
          'vai-my-2',
          'vai-px-4 vai-py-2',
          'vai-flex vai-items-center vai-justify-left',
          'vai-rounded-sm',
          {
            'vai-bg-zinc-800': mode === 'dark',
            'vai-bg-zinc-100': mode === 'light',
          },
        )}
      >
        <span
          className={classNames(
            'vai-text-sm vai-font-medium',
            'vai-block',
            'vai-text-center vai-items-center',
            'vai-font-inter',
            {
              'vai-text-foreground-dark': mode === 'dark',
              'vai-text-zinc-900': mode === 'light',
            },
          )}
        >
          <Trans>AI Findings to Include</Trans>
        </span>
      </div>
      <div
        className={classNames(
          'vai-flex vai-flex-col vai-gap-2',
          'vai-px-2 vai-mt-2',
        )}
      >
        {findingTypes.map((findingType) => {
          // Map finding types to categories for toggle behavior
          let category: FindingsCategory | null = null;
          if (findingType.value === 'caries') category = 'restorative';
          else if (findingType.value === 'incipients') category = 'incipients';
          else if (findingType.value === 'radiographic_boneloss')
            category = 'periodontal';
          else if (findingType.value === 'periapical_radiolucency')
            category = 'endodontic';

          return (
            <div
              key={findingType.value}
              className={classNames(
                'vai-flex vai-items-center vai-gap-2 vai-cursor-pointer',
                'vai-py-2',
                'vai-border-b',
                {
                  'vai-border-b-sidebar-border': mode === 'dark',
                  'vai-border-b-zinc-200': mode === 'light',
                },
              )}
            >
              <Checkbox
                checked={
                  category
                    ? selectedFindingsCategories.includes(category)
                    : enabledFindingTypes[findingType.value]
                }
                id={`finding-checkbox-${findingType.value}`}
                onCheckedChange={(checked: boolean) => {
                  if (category) {
                    handleToggleClick(category, checked);
                  } else {
                    setEnabledFindingTypes({
                      ...enabledFindingTypes,
                      [findingType.value]: checked,
                    });
                  }
                }}
              />
              <Label
                className={classNames(
                  'vai-text-sm vai-font-medium vai-font-inter vai-cursor-pointer',
                  {
                    'vai-text-foreground-dark': mode === 'dark',
                    'vai-text-zinc-900': mode === 'light',
                  },
                )}
                htmlFor={`finding-checkbox-${findingType.value}`}
              >
                {findingType.label}
              </Label>
            </div>
          );
        })}
        <div
          className={classNames(
            'vai-flex vai-items-center vai-gap-2 vai-cursor-pointer',
            'vai-py-2',
            'vai-border-b',
            {
              'vai-border-b-sidebar-border': mode === 'dark',
              'vai-border-b-zinc-200': mode === 'light',
            },
          )}
        >
          <Checkbox
            checked={selectedFindingsCategories.includes('chart')}
            id="tooth-anatomy-checkbox"
            onCheckedChange={(checked: boolean) =>
              handleToggleClick('chart', checked)
            }
          />
          <Label
            htmlFor="tooth-anatomy-checkbox"
            className={classNames(
              'vai-text-sm vai-font-medium vai-font-inter vai-cursor-pointer',
              {
                'vai-text-foreground-dark': mode === 'dark',
                'vai-text-zinc-900': mode === 'light',
              },
            )}
          >
            <Trans>Tooth Anatomy</Trans>
          </Label>
        </div>
      </div>
    </div>
  );
};
